import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { MenuItem } from '@/queries/useMenuItems'
import { Edit2, Eye, EyeOff, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MenuItemForm from './MenuItemForm'

interface MenuItemsListProps {
  items: MenuItem[]
  onRefresh: () => void
  isLoading?: boolean
  onToggleAvailability: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onUpdate: (id: string, data: Partial<MenuItem>) => Promise<void>
  onBulkUpdate: (ids: string[], data: Partial<MenuItem>) => Promise<void>
  onImageUpload: (id: string, file: File) => Promise<string>
}

export default function MenuItemsList({
  items,
  onRefresh,
  onToggleAvailability,
  onDelete,
  onUpdate,
  onBulkUpdate,
  onImageUpload,
}: MenuItemsListProps) {
  const { t } = useTranslation('admin')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const toggleAllSelection = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map(item => item.id)))
    }
  }

  const handleBulkToggleAvailability = async () => {
    const isCurrentlyAvailable = items.find(item => selectedIds.has(item.id))?.isAvailable
    try {
      await onBulkUpdate(Array.from(selectedIds), { isAvailable: !isCurrentlyAvailable })
      setSelectedIds(new Set())
      onRefresh()
    } catch (error) {
      console.error('Failed to bulk update:', error)
    }
  }

  const formatPrice = (piasters: number): string => {
    return (piasters / 100).toFixed(1)
  }

  return (
    <div className="w-full space-y-4">
      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <span className="text-sm font-medium">
            {t('menuItems.itemsSelected', { count: selectedIds.size })}
          </span>
          <Button
            size="sm"
            onClick={handleBulkToggleAvailability}
            variant="outline"
          >
            {t('menuItems.toggleAvailability')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelectedIds(new Set())}
          >
            {t('menuItems.clearSelection')}
          </Button>
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={selectedIds.size === items.length && items.length > 0}
                onChange={toggleAllSelection}
                aria-label="Select all items"
              />
            </TableHead>
            <TableHead className="w-24">{t('menuItems.table.image')}</TableHead>
            <TableHead>{t('menuItems.table.name')}</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Details</TableHead>
            <TableHead>{t('menuItems.table.price')}</TableHead>
            <TableHead>{t('menuItems.table.status')}</TableHead>
            <TableHead className="text-right">{t('menuItems.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {t('menuItems.emptyState')}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} className="hover:bg-gray-50">
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelection(item.id)}
                      aria-label={`Select ${item.name}`}
                    />
                  </TableCell>
                  <TableCell>
                    {(item.img_url || item.imageUrl) && (
                      <div className="w-20 h-20 rounded overflow-hidden">
                        <img
                          src={item.img_url || item.imageUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{item.name}</div>
                      {(item.name_ar || item.nameAr) && (
                        <div className="text-sm text-muted-foreground" dir="rtl">
                          {item.name_ar || item.nameAr}
                        </div>
                      )}
                      {item.prep_time_minutes && (
                        <div className="text-xs text-gray-500 mt-1">
                          ⏱️ {item.prep_time_minutes} min
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.category && typeof item.category === 'object' && (
                      <Badge variant="outline" className="text-xs">
                        {item.category.name}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {item.ingredients && item.ingredients.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.ingredients.slice(0, 2).map((ing: string, idx: number) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {ing}
                            </Badge>
                          ))}
                          {item.ingredients.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{item.ingredients.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                      {item.dietary && item.dietary.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.dietary.map((diet: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-green-50">
                              {diet}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {item.allergens && item.allergens.length > 0 && (
                        <div className="text-xs text-red-600">
                          ⚠️ {item.allergens.join(', ')}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatPrice(item.price)} EGP
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.isAvailable ? 'default' : 'secondary'}>
                      {item.isAvailable ? t('menuItems.available') : t('menuItems.unavailable')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Toggle Availability */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onToggleAvailability(item.id)}
                        title={item.isAvailable ? 'Make unavailable' : 'Make available'}
                      >
                        {item.isAvailable ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </Button>

                      {/* Edit */}
                      <Dialog open={editingId === item.id} onOpenChange={(open) => {
                        if (!open) setEditingId(null)
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingId(item.id)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>{t('menuItems.editTitle')}</DialogTitle>
                            <DialogDescription>
                              {t('menuItems.editDescription')}
                            </DialogDescription>
                          </DialogHeader>
                          <MenuItemForm
                            menuItem={item}
                            onSubmit={async (data) => {
                              await onUpdate(item.id, data)
                              setEditingId(null)
                              onRefresh()
                            }}
                            onImageUpload={async (id, file) => {
                              const imageUrl = await onImageUpload(id, file)
                              // Image upload already refreshes the list
                              return imageUrl
                            }}
                            onCancel={() => setEditingId(null)}
                          />
                        </DialogContent>
                      </Dialog>

                      {/* Delete */}
                      <Dialog open={deleteConfirmId === item.id} onOpenChange={(open) => {
                        if (!open) setDeleteConfirmId(null)
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(item.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{t('menuItems.deleteTitle')}</DialogTitle>
                            <DialogDescription>
                              {t('menuItems.deleteDescription', { name: item.name })}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              {t('common:buttons.cancel')}
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={async () => {
                                await onDelete(item.id)
                                setDeleteConfirmId(null)
                                onRefresh()
                              }}
                            >
                              {t('common:buttons.delete')}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
    </div>
  )
}
